import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Download,
  Globe2,
  Mail,
  Radar,
  Sparkles,
  Radio,
  Video,
} from 'lucide-react';
import { CheckItem, DiscordGlyph, Footer, Header, SourceGlyph, SpiderMark, StatusPill } from './components.jsx';
import { money } from './api.js';

const audiences = [
  'Video Game Teams',
  'Video Game Developers',
  'Video Game Publishers',
  'Indie Game Developers',
  'Solo Game Developers',
];

const signals = [
  { source: 'Web', title: 'News mention', outlet: 'Indie Signal', time: '2m', position: 'press' },
  { source: 'YouTube', title: 'New video', outlet: 'Pixel Harbour', time: '18m', position: 'youtube' },
  { source: 'Twitch', title: 'Stream signal', outlet: 'mossyplays', time: '42m', position: 'twitch' },
];

function RotatingAudience() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % audiences.length), 2800);
    return () => window.clearInterval(timer);
  }, []);
  return <span className="rotating-audience" key={audiences[index]} aria-hidden="true">{audiences[index]}</span>;
}

export function Landing() {
  const [currency, setCurrency] = useState('USD');
  const [games, setGames] = useState(1);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    fetch('/api/config').then((response) => response.json()).then((data) => setCatalog(data.catalog || [])).catch(() => {});
  }, []);
  const offer = (key, fallback) => catalog.find((item) => item.product_key === key) || fallback;
  const free = offer('free_watch', { name: 'Free Watch', cta_label: 'Start Free', features: ['1 game included', 'Web monitoring and dashboard', 'Daily crawl', 'CSV mention export'], price_usd_cents: 0, price_eur_cents: 0, price_gbp_cents: 0 });
  const crawler = offer('crawler', { name: 'Crawler', cta_label: 'Spin my web', features: ['YouTube and Twitch included (TikTok, coming soon)', 'Unlimited custom RSS sources', 'Email and Discord bot delivery', 'Cancel anytime'], price_usd_cents: 1900, price_eur_cents: 1900, price_gbp_cents: 1700 });
  const quick = offer('quick_catch', { name: 'Quick Catch', cta_label: 'Catch mentions now', features: ['7-day priority watch', 'All configured sources', 'Dashboard trail and CSV export', 'Results retained for 90 days'], price_usd_cents: 2900, price_eur_cents: 2900, price_gbp_cents: 2500 });
  const selectedPrice = (item) => Number(item[`price_${currency.toLowerCase()}_cents`] || 0);
  return (
    <div className="landing-page">
      <Header />
      <main>
        <section className="hero shell">
          <div className="hero-copy">
            <span className="eyebrow audience-eyebrow"><Radar size={15} /><span aria-hidden="true">Mention Monitoring for <RotatingAudience /></span><span className="sr-only">Mention monitoring for video game teams, developers, publishers, indie developers and solo developers.</span></span>
            <h1>Spin a web.<br /><em>Catch every mention.</em></h1>
            <p>Press Spider crawls news, YouTube and Twitch for mentions of your game. It then delivers every mention as it happens, or as a digest, to your inbox or Discord server.</p>
            <div className="hero-actions">
              <div className="primary-cta"><a className="button" href="/app">Start with Free <ArrowRight size={17} /></a><small>No credit card required</small></div>
              <a className="button button-quiet" href="#pricing">See pricing</a>
            </div>
            <div className="hero-proof"><span><Globe2 size={15} /> Web included</span><span><Download size={15} /> CSV export</span><span><DiscordGlyph size={15} /> Discord-ready</span></div>
          </div>
          <div className="catch-visual" aria-label="Illustrative list of mentions caught across the web">
            <div className="catch-window">
              <div className="catch-window-head">
                <div><span className="live-dot" /> Crawling</div>
                <span>3 new mentions</span>
              </div>
              <div className="signal-web">
                <svg className="signal-web-lines" viewBox="0 0 560 350" preserveAspectRatio="none" aria-hidden="true">
                  <g>
                    <line x1="280" y1="175" x2="280" y2="4" /><line x1="280" y1="175" x2="430" y2="28" />
                    <line x1="280" y1="175" x2="556" y2="175" /><line x1="280" y1="175" x2="470" y2="338" />
                    <line x1="280" y1="175" x2="280" y2="346" /><line x1="280" y1="175" x2="84" y2="338" />
                    <line x1="280" y1="175" x2="4" y2="175" /><line x1="280" y1="175" x2="116" y2="24" />
                    <polygon points="280,136 315,142 344,175 324,213 280,224 235,212 216,175 246,142" />
                    <polygon points="280,93 356,104 410,175 371,259 280,282 188,258 150,175 205,102" />
                    <polygon points="280,48 402,61 492,175 420,306 280,328 137,304 68,175 156,58" />
                  </g>
                  <circle cx="280" cy="175" r="56" />
                </svg>
                <div className="web-core"><SpiderMark size={46} /><span><strong>Moonlake</strong><small>3 signals connected</small></span></div>
                {signals.map((item) => (
                  <article className={`signal-node signal-${item.position}`} key={item.source}>
                    <span className={`source-dot ${item.source.toLowerCase()}`}><SourceGlyph source={item.source.toLowerCase() === 'web' ? 'press' : item.source.toLowerCase()} size={15} /></span>
                    <div><strong>{item.title}</strong><small>{item.outlet} · {item.source}</small></div>
                    <time>{item.time}</time>
                  </article>
                ))}
              </div>
              <div className="delivery-strip"><Mail size={15} /> Email sent <span /> <DiscordGlyph size={15} /> Posted to #press-watch</div>
            </div>
          </div>
        </section>

        <section className="section section-tinted" id="how">
          <div className="shell">
            <div className="section-head compact">
              <div><span className="section-number">How it works</span><h2>Spin a web in<br />three small steps.</h2></div>
            </div>
            <div className="steps-grid">
              <Step number="1" title="Name the game">Add the title, alternate spellings, and any Steam, Epic Games Store, or GOG pages.</Step>
              <Step number="2" title="Pick the sources">Web is free. Video and stream monitoring is included in paid plans, with unlimited custom web sources via RSS.</Step>
              <Step number="3" title="Choose delivery">Any mentions caught will be delivered by email or the Olamia AI Discord bot.</Step>
            </div>
            <p className="how-summary">Mentions are scattered. Stop manual searching. Let Press Spider crawl and capture your mentions as they happen.</p>
          </div>
        </section>

        <section className="section shell pricing-section" id="pricing">
          <div className="section-head">
            <div><span className="section-number">Pricing</span><h2>Start free.<br />Add more legs later.</h2></div>
            <div className="pricing-controls"><span>Show prices in</span><div className="currency-switch" aria-label="Currency">{['USD', 'EUR', 'GBP'].map((item) => <button className={currency === item ? 'active' : ''} onClick={() => setCurrency(item)} key={item}>{({ USD: '$', EUR: '€', GBP: '£' })[item]}</button>)}</div></div>
          </div>
          <div className="pricing-grid">
            <PriceCard name={free.name} price={money(selectedPrice(free), currency)} note="forever" cta={free.cta_label} href="/app">
              {(free.features || []).map((feature) => <CheckItem key={feature}>{feature}</CheckItem>)}
            </PriceCard>
            <PriceCard name={crawler.name} price={money(selectedPrice(crawler), currency)} note="/ month per game" cta={crawler.cta_label} href={`/app?plan=crawler&games=${games}&currency=${currency}`} featured>
              <li className="game-quantity"><label htmlFor="landing-games">Games to monitor</label><select id="landing-games" value={games} onChange={(event) => setGames(Number(event.target.value))}>{[1, 2, 3, 4, 5, 10].map((count) => <option value={count} key={count}>{count}</option>)}</select></li>
              {(crawler.features || []).map((feature) => <CheckItem key={feature}>{feature}</CheckItem>)}
              <li className="price-total"><span>{games} {games === 1 ? 'game' : 'games'}</span><strong>{money(selectedPrice(crawler) * games, currency)}/month</strong></li>
            </PriceCard>
            <PriceCard name={quick.name} price={`from ${money(selectedPrice(quick), currency)}`} note="one-off per game" cta={quick.cta_label} href={`/app?plan=quick-catch&currency=${currency}`}>
              {(quick.features || []).map((feature) => <CheckItem key={feature}>{feature}</CheckItem>)}
            </PriceCard>
          </div>
          <div className="subscription-value"><strong>Why subscribe?</strong><span>One month of continuous Crawler monitoring costs less than one seven-day Quick Catch. It also includes ongoing alerts, unlimited RSS sources, and monitoring history while your subscription remains active.</span></div>
        </section>

        <section className="section faq-section shell" id="faq">
          <div className="section-head compact"><div><span className="section-number">FAQ</span><h2>Loose threads,<br />tied up.</h2></div></div>
          <div className="faq-list">
            <Faq q="What counts as a mention?">A result whose title or description matches your game title or one of the alternate names you provide. You can inspect and export every stored mention.</Faq>
            <Faq q="How does the Discord bot work?">Click “Add to Discord”, choose a server and channel, and the Olamia AI bot posts new mentions there. No webhook copying. The bot also supports status and latest-mention commands.</Faq>
            <Faq q="Can I use it without a subscription?">Yes. The Web source, your Web and CSV export stay free. Subscriptions pay for faster crawling, delivery automation and extra sources.</Faq>
            <Faq q="Will it find all mentions?">No index covers the whole internet. Press Spider gives you a repeatable monitoring trail, not a guarantee that every page or video platform has indexed every mention.</Faq>
            <Faq q="Why is TikTok on a waitlist?">TikTok’s broad public-content search is restricted to approved research access. Press Spider will only enable it through a legitimate licensed provider rather than fragile scraping.</Faq>
            <Faq q="Will you monitor other social media platforms in the future?">Yes, we plan to expand our crawling capabilities as we grow.</Faq>
            <Faq q="Can you build a custom version?">Yes. Press Spider started out as an internal tool built for making monitoring and reporting at launch easier. We have now created a product from it, and are able to create and deploy custom versions.</Faq>
            <Faq q="Do you have a roadmap?">Soon (tm)</Faq>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ChannelCard({ icon, title, status, tone, children }) {
  return <article className="channel-card"><div className={`channel-icon ${tone}`}>{icon}</div><div className="channel-title"><h3>{title}</h3><StatusPill tone={tone}>{status}</StatusPill></div><p>{children}</p></article>;
}

function Step({ number, title, children }) {
  return <article className="step-card"><span>{number}</span><h3>{title}</h3><p>{children}</p></article>;
}

function PriceCard({ name, price, note, cta, href, featured, children }) {
  return (
    <article className={`price-card ${featured ? 'featured' : ''}`}>
      {featured && <span className="popular-tag">Best ongoing value</span>}
      <h3>{name}</h3><div className="price"><strong>{price}</strong><span>{note}</span></div>
      <ul className="check-list">{children}</ul>
      <a className={`button ${featured ? '' : 'button-quiet'}`} href={href}>{cta}<ArrowRight size={16} /></a>
    </article>
  );
}

function Faq({ q, children }) {
  return <details><summary>{q}<span>+</span></summary><p>{children}</p></details>;
}
